import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react';

import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import {useShopperInventoryIds} from '@amblique/king-living/app/hooks/use-shopper-inventory-ids';
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui';

import {
  buildSteps,
  getCustomizeStepSlotKind,
  getStepTitle,
  getVisibleCustomizeSteps,
  getVisibleStepDisplayIndex,
  findFirstIncompleteVisibleStep,
  isMultiSlotCustomizeStep,
  isStepCompleteUtil,
} from '@amblique/king-living/app/hooks/use-customize-steps';
import {useStepScrollHeader} from '@amblique/king-living/app/hooks/use-step-scroll-header';
import {GET_PRODUCTS_PARAMS, resolveCoverVariantViewDetailsProduct} from '@amblique/king-living/app/utils/api-utils';
import {
  applySiblingSlotMatchesByDisplayName,
  buildOptionSlotSelectionFromProduct,
  formatOptionProductSelectionName,
} from '@amblique/king-living/app/utils/customize-selections';
import {hasCustomizeOptionSelections, resolveCustomizeStepsGalleryState, resolveMostRecentCommittedOptionProductId} from '@amblique/king-living/app/utils/gallery-state-utils';

/** Match a getProducts batch row to the requested id (keepPreviousData may return stale rows). */
function findFetchedProductById(items, productId) {
  if (!productId || !items?.length) return null;

  return items.find((item) => {
    const id = item?.id ?? item?.productId;

    return id != null && String(id) === String(productId);
  }) ?? null;
}

/** React Query in-flight for an enabled getProducts call. */
function isProductsFetchInFlight(enabled, isLoading, isFetching) {
  return Boolean(enabled) && (isLoading || isFetching);
}

/** Complex flyout: retain LHS only while getProducts for productId is actively in flight. */
function isComplexFlyoutScapiViewDetailsPending(fromParent, productId, fromFetch, isFetchInFlight) {
  return fromParent === undefined && Boolean(productId) && !fromFetch && isFetchInFlight;
}

/**
 * Encapsulates all state, refs, and interaction handlers for CustomizeStepsContent.
 *
 * KINDW-1124 — controlled `activeSlot`:
 *   When `onActiveSlotChange` is provided, `activeSlot` is treated as a controlled
 *   value (read from `controlledActiveSlot`, written via `onActiveSlotChange`).
 *   This allows two `<CustomizeStepsContent>` instances (one inline rendering the
 *   steps view, one inside a Drawer rendering the picker view) to share the same
 *   active-slot state — required so the Configurable Master "Choose" interaction
 *   opens a real flyout overlay instead of swapping inline content (FSD §8.2,
 *   PDP-07: Customise Your Own). When `onActiveSlotChange` is omitted (Complex PDP
 *   and any other existing caller), the hook falls back to internal state and
 *   behaves exactly as before.
 */
export function useCustomizeStepsContent({
  controlledActiveSlot,
  coverGroups,
  coverVariantProduct: coverVariantProductFromParent,
  committedOptionProduct: committedOptionProductFromParent,
  drawerHeight,
  hasUserInteracted = false,
  isSimplePDP = false,
  onActiveSlotChange,
  onFlyoutStepEverComplete,
  onGalleryProductChange,
  onHeaderChange,
  onCoverPickerOpen,
  onSelect,
  onSlotProductPreview,
  onSubViewChange,
  onUserInteract,
  onViewDetails,
  product,
  selections,
}) {
  const intl = useIntl();
  const isMobile = useBreakpointValue({base: true, lg: false});

  const STEPS = useMemo(
    () => buildSteps(product, {isSimplePDP}),
    [product, isSimplePDP]
  );

  const [internalActiveSlot, setInternalActiveSlot] = useState(null);
  const isActiveSlotControlled = typeof onActiveSlotChange === 'function';
  const activeSlot = isActiveSlotControlled ?
    (controlledActiveSlot ?? null) :
    internalActiveSlot;
  // Keep the setter identity-stable (matches useState setter semantics) so the
  // many useCallback/useEffect deps below that DON'T include it remain correct.
  // The latest controlled inputs are read from a ref at call time.
  const activeSlotControlRef = useRef({
    activeSlot,
    isControlled: isActiveSlotControlled,
    onChange: onActiveSlotChange,
  });

  activeSlotControlRef.current = {
    activeSlot,
    isControlled: isActiveSlotControlled,
    onChange: onActiveSlotChange,
  };
  const setActiveSlot = useCallback((next) => {
    const {activeSlot: current, isControlled, onChange} = activeSlotControlRef.current;

    if (isControlled) {
      const value = typeof next === 'function' ? next(current) : next;

      onChange(value);
    } else {
      setInternalActiveSlot(next);
    }
  }, []);
  const [activeStepId, setActiveStepId] = useState(null);
  const [galleryPreviewProduct, setGalleryPreviewProduct] = useState(null);
  const [optionPreviewCleared, setOptionPreviewCleared] = useState(null);
  const [liveMessage, setLiveMessage] = useState('');

  const isUserScrollingRef = useRef(false);
  const lastCompletedSlotRef = useRef(null);
  // Last product committed with Next per "${stepId}-${slotIndex}"; Back reverts preview to this.
  const confirmedProductPerStep = useRef({});
  const scrollBodyRef = useRef(null);
  const slotButtonRefs = useRef({});
  const stepRefs = useRef({});
  // Carries the just-completed step from handleSlotNext into the post-render scroll effect.
  // A setTimeout inside the effect gives iOS Safari time to fully paint the DOM before scrolling.
  const pendingScrollStepRef = useRef(null);
  // Latest committed cover SCAPI product for View Details cache reuse.
  const coverVariantProductRef = useRef(null);

  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const inventoryIds = useShopperInventoryIds();

  const isStepComplete = useCallback(
    (stepId) => isStepCompleteUtil(stepId, selections, STEPS),
    [selections, STEPS]
  );

  useEffect(() => {
    STEPS.forEach((step) => {
      if (isStepComplete(step.id)) {
        onFlyoutStepEverComplete?.(step.id);
      }
    });
  }, [STEPS, isStepComplete, onFlyoutStepEverComplete]);

  const handleBack = useCallback(() => {
    if (activeSlot) {
      const key = `${activeSlot.stepId}-${activeSlot.slotIndex}`;

      setGalleryPreviewProduct(
        activeSlot.stepId === 'cover' ? null : (confirmedProductPerStep.current[key] ?? null)
      );
    }
    setOptionPreviewCleared(null);
    setActiveSlot(null);
    onHeaderChange?.(null);
  }, [activeSlot, onHeaderChange]);

  const handleStepInteraction = useCallback((stepId) => {
    const step = STEPS.find((s) => s.id === stepId);

    if (!step) return;

    setActiveStepId(stepId);
    onHeaderChange?.({
      isMultiSlotStep: isMultiSlotCustomizeStep(step),
      customizeStepKind: getCustomizeStepSlotKind(step),
      stepIndex: getVisibleStepDisplayIndex(stepId, STEPS),
      title: getStepTitle(step, intl),
      totalSteps: getVisibleCustomizeSteps(STEPS).length,
    });
  }, [STEPS, onHeaderChange, intl]);

  const handleSlotChoose = useCallback((stepId, slotIndex) => {
    handleStepInteraction(stepId);
    onUserInteract?.();
    const restored =
      stepId === 'cover' ? null : (confirmedProductPerStep.current[`${stepId}-${slotIndex}`] ?? null);

    setGalleryPreviewProduct(restored);
    setOptionPreviewCleared(null);
    // Capture whether a selection already existed BEFORE opening the picker.
    // For cover, the selection is committed immediately on pick (not deferred to Next),
    // so we must snapshot this here rather than reading selections inside handleSlotNext.
    const hadExistingSelection =
      stepId === 'cover' ? !!selections?.cover?.productId : !!selections?.[stepId]?.[slotIndex]?.productId;

    setActiveSlot({slotIndex, stepId, hadExistingSelection});

    if (stepId === 'cover' && isSimplePDP) {
      onCoverPickerOpen?.();
    }
  }, [handleStepInteraction, isSimplePDP, onCoverPickerOpen, onUserInteract, selections]);

  const handleSlotProductPreview = useCallback((previewProduct) => {
    setGalleryPreviewProduct(previewProduct);
    setOptionPreviewCleared(
      previewProduct === null && activeSlot ?
        `${activeSlot.stepId}-${activeSlot.slotIndex}` :
        null
    );
    // Complex: gallery effect writes context; Simple still previews hero on tap.
    if (isSimplePDP) {
      onSlotProductPreview?.(previewProduct);
    }
  }, [activeSlot, isSimplePDP, onSlotProductPreview]);

  const handleCoverVariantViewDetails = useCallback(async (variant, viewOptions = {}) => {
    const {coverItemType} = viewOptions;
    const detailProduct = await resolveCoverVariantViewDetailsProduct({
      variant,
      masterProduct: product,
      selectedCoverProductId: selections?.cover?.productId ?? null,
      cachedCoverVariantProduct: coverVariantProductRef.current,
      shopperProducts: api.shopperProducts,
      getTokenWhenReady,
    });

    onViewDetails?.(
      detailProduct,
      coverItemType != null && coverItemType !== '' ? {coverItemType} : {}
    );
  }, [
    api.shopperProducts,
    getTokenWhenReady,
    onViewDetails,
    product,
    selections?.cover?.productId
  ]);

  /** Option-slot "View details" — product header when multi-type slot-cards step. */
  const handleOptionProductViewDetails = useCallback(
    async (pickerProduct) => {
      if (!activeSlot || activeSlot.stepId === 'cover') {
        onViewDetails?.(pickerProduct);

        return;
      }

      const step = STEPS.find((s) => s.id === activeSlot.stepId);
      const productDetailHeaderUi = isMultiSlotCustomizeStep(step);

      const clickedId = pickerProduct?.id ?? pickerProduct?.productId ?? null;
      let detailProduct = pickerProduct;

      if (clickedId) {
        try {
          const token = await getTokenWhenReady();
          const res = await api.shopperProducts.getProducts({
            headers: {Authorization: `Bearer ${token}`},
            parameters: {
              ids: clickedId,
              ...GET_PRODUCTS_PARAMS,
              ...(inventoryIds ? {inventoryIds} : {}),
            },
          });

          detailProduct = res?.data?.[0] ?? pickerProduct;
        } catch {
          detailProduct = pickerProduct;
        }
      }

      onViewDetails?.(detailProduct, {productDetailHeaderUi});
    },
    [
      activeSlot,
      STEPS,
      api.shopperProducts,
      getTokenWhenReady,
      inventoryIds,
      intl,
      onViewDetails
    ]
  );

  const handleSlotNext = useCallback(() => {
    if (!activeSlot) return;

    const step = STEPS.find((s) => s.id === activeSlot.stepId);
    const slotKey = `${activeSlot.stepId}-${activeSlot.slotIndex}`;

    if (activeSlot.stepId !== 'cover') {
      if (!step?.container) return;

      const {container} = step;
      const value = container.values?.[activeSlot.slotIndex];

      if (!value) return;

      const row = [...(selections?.[activeSlot.stepId] || [])];
      const existing = row[activeSlot.slotIndex];

      if (galleryPreviewProduct) {
        setOptionPreviewCleared(null);
        row[activeSlot.slotIndex] = buildOptionSlotSelectionFromProduct(
          galleryPreviewProduct,
          activeSlot.slotIndex,
          value,
          container
        );

        // After confirming a slot, propagate to empty sibling slots whose displayed
        // name matches (e.g. shelves — FSD: both empty → one pick fills both).
        // Skips siblings that already have a selection. Page-load preselect stays
        // off on Simple PDP via mergePreselectedSelectionsFromProduct disablePreselect.
        const slotIdsBeforeSync = row.map((slot) => slot?.productId ?? null);
        const syncedRow = applySiblingSlotMatchesByDisplayName(
          container,
          row,
          activeSlot.slotIndex,
          galleryPreviewProduct
        );

        onSelect(container.containerId, syncedRow);

        syncedRow.forEach((sel, j) => {
          if (j === activeSlot.slotIndex) return;
          if (slotIdsBeforeSync[j] != null || !sel?.productId) return;

          confirmedProductPerStep.current[`${activeSlot.stepId}-${j}`] = {
            id: sel.productId,
            imageGroups: [],
            name: sel.name,
          };
        });
      } else if (existing?.productId && optionPreviewCleared === slotKey) {
        row[activeSlot.slotIndex] = null;
        onSelect(container.containerId, row);
        delete confirmedProductPerStep.current[slotKey];
        setOptionPreviewCleared(null);
      } else if (!existing?.productId) {
        return;
      }
    }

    isUserScrollingRef.current = false;
    lastCompletedSlotRef.current = {slotIndex: activeSlot.slotIndex, stepId: activeSlot.stepId};

    if (galleryPreviewProduct) {
      confirmedProductPerStep.current[slotKey] = galleryPreviewProduct;
    }

    const announceName =
      activeSlot.stepId === 'cover' ?
        selections?.cover?.name :
        (formatOptionProductSelectionName(galleryPreviewProduct) ||
          selections?.[activeSlot.stepId]?.[activeSlot.slotIndex]?.name);

    if (announceName) {
      const stepLabel = step ? getStepTitle(step, intl) : activeSlot.stepId;

      setLiveMessage(`${stepLabel} selected: ${announceName}.`);
    }

    // Always advance to the next incomplete step after Done — including when the
    // user re-selects ("Change"). KINDW-1244 feature: confirming an option flows
    // straight into the next option container.
    pendingScrollStepRef.current = step;
    setOptionPreviewCleared(null);
    setActiveSlot(null);
  }, [activeSlot, galleryPreviewProduct, optionPreviewCleared, STEPS, selections, onSelect, intl, isSimplePDP]);

  const handleMainNext = useCallback(() => {
    const nextStep = findFirstIncompleteVisibleStep(STEPS, isStepComplete);

    if (nextStep && stepRefs.current[nextStep.id]) {
      stepRefs.current[nextStep.id].scrollIntoView({behavior: 'smooth', block: 'start'});
      setActiveStepId(nextStep.id);
      onHeaderChange?.({
        isMultiSlotStep: isMultiSlotCustomizeStep(nextStep),
        customizeStepKind: getCustomizeStepSlotKind(nextStep),
        stepIndex: getVisibleStepDisplayIndex(nextStep.id, STEPS),
        title: getStepTitle(nextStep, intl),
        totalSteps: getVisibleCustomizeSteps(STEPS).length,
      });
    }
  }, [STEPS, isStepComplete, onHeaderChange, intl]);

  // Update header when entering sub-view.
  //
  // Simple PDP (FSD §SECTION-PDP-SIMPLE-08): picker header "Choose {typeName}", no step counter.
  // Complex PDP (KINDW-1331): same slot title when typeName is set; step counter stays visible.
  useEffect(() => {
    onSubViewChange?.(activeSlot !== null);
    if (activeSlot !== null) {
      setActiveStepId(null);
      const step = STEPS.find((s) => s.id === activeSlot.stepId);

      if (step) {
        const isOptionSlot = activeSlot.stepId !== 'cover';
        const slotValue = isOptionSlot ?
          step.container?.values?.[activeSlot.slotIndex] :
          null;
        const slotLabel = slotValue?.typeName?.trim() || null;
        const useSimpleSlotTitle = isSimplePDP && isOptionSlot && slotLabel;
        const useComplexSlotPickerTitle = !isSimplePDP && isOptionSlot && slotLabel;
        const useSlotPickerTitle = useSimpleSlotTitle || useComplexSlotPickerTitle;
        const title = useSlotPickerTitle ?
          intl.formatMessage(
            {
              defaultMessage: 'Choose {slotName}',
              id: 'customize_panel.picker.header_title',
            },
            {slotName: slotLabel}
          ) :
          getStepTitle(step, intl);

        onHeaderChange?.({
          isMultiSlotStep: isMultiSlotCustomizeStep(step),
          customizeStepKind: getCustomizeStepSlotKind(step),
          onBack: handleBack,
          stepIndex: useSimpleSlotTitle ? null : getVisibleStepDisplayIndex(activeSlot.stepId, STEPS),
          title,
          totalSteps: useSimpleSlotTitle ? null : getVisibleCustomizeSteps(STEPS).length,
        });
      }
    }
  }, [activeSlot, onSubViewChange, STEPS, onHeaderChange, handleBack, intl, isSimplePDP]);

  // KINDW-1124 — cleanup when leaving a sub-view by ANY path.
  // handleBack performs preview reset + header clear inline; this effect mirrors
  // that behaviour for transitions caused by external setters (e.g. the
  // Configurable Master flyout drawer overlay click → CustomizeStepsInline
  // setActiveSlot(null)). prevActiveSlotRef ensures we only restore on the
  // null-transition, never on initial mount where activeSlot starts at null.
  const prevActiveSlotRef = useRef(null);

  useEffect(() => {
    const prev = prevActiveSlotRef.current;

    prevActiveSlotRef.current = activeSlot;

    if (activeSlot !== null || prev === null) return;

    const key = `${prev.stepId}-${prev.slotIndex}`;

    setGalleryPreviewProduct(
      prev.stepId === 'cover' ? null : (confirmedProductPerStep.current[key] ?? null)
    );
    setOptionPreviewCleared(null);
    onHeaderChange?.(null);
  }, [activeSlot, onHeaderChange]);

  // Picker + main are separate hook instances — reset cleared-slot key whenever activeSlot
  // changes (including re-Choose on the same slot from the main steps list).
  useEffect(() => {
    if (!activeSlot) return;

    setOptionPreviewCleared(null);
  }, [activeSlot]);

  // Derive cover variation values from the current selections so the gallery can
  // merge cover cylindoFeature (DE-790 — master c_cylindoId stays the product code).
  const coverVariationValues = selections?.cover?.variationValues ?? null;

  const selectedCoverId = selections?.cover?.productId ?? null;
  const isCoverPickerOpen = activeSlot?.stepId === 'cover';

  const shouldFetchCoverVariant =
    coverVariantProductFromParent === undefined && Boolean(selectedCoverId);

  const {
    data: variantProductResult,
    isFetching: isCoverVariantFetching,
    isLoading: isCoverVariantLoading,
  } = useProducts(
    {parameters: {ids: selectedCoverId ?? '', ...GET_PRODUCTS_PARAMS}},
    {enabled: shouldFetchCoverVariant, keepPreviousData: true} // hold prior cover while id refetches
  );
  const coverVariantProductFromFetch = findFetchedProductById(
    variantProductResult?.data,
    selectedCoverId
  );
  const coverVariantProduct = coverVariantProductFromParent !== undefined ?
    (coverVariantProductFromParent ?? null) :
    coverVariantProductFromFetch;

  coverVariantProductRef.current = coverVariantProduct;

  // Recency: most-recently-CHANGED non-cover option productId from committed `selectedAt`
  // (buildOptionSlotSelectionFromProduct). Simple PDP — survives refresh when selections
  // persist (sessionStorage) but `galleryPreviewProduct` does not (e.g. ETO Compact Desk).
  // Complex flyout — main steps when preview is null; `recencyExcludeSlot` drops the slot
  // the user deselected in the picker before Next.
  //
  // Recency (not catalog order) so ViewDetails matches the latest option layer. Ties use
  // `>=` on STEPS slot order. See propagation effect below.
  const recencyExcludeStepId = !isSimplePDP &&
    optionPreviewCleared &&
    activeSlot &&
    activeSlot.stepId !== 'cover' &&
    !galleryPreviewProduct &&
    optionPreviewCleared === `${activeSlot.stepId}-${activeSlot.slotIndex}` ?
    activeSlot.stepId :
    null;
  const recencyExcludeSlotIndex = recencyExcludeStepId != null ? activeSlot.slotIndex : null;
  const recencyExcludeSlot = recencyExcludeStepId != null ?
    {slotIndex: recencyExcludeSlotIndex, stepId: recencyExcludeStepId} :
    null;

  const mostRecentCommittedOptionProductId = useMemo(() => {
    if (!STEPS?.length || !selections) return null;
    if (isSimplePDP) {
      return resolveMostRecentCommittedOptionProductId(selections, STEPS);
    }

    if (
      galleryPreviewProduct ||
      isCoverPickerOpen ||
      (!recencyExcludeSlot && !hasCustomizeOptionSelections(selections))
    ) {
      return null;
    }

    return resolveMostRecentCommittedOptionProductId(selections, STEPS, recencyExcludeSlot);
  }, [
    STEPS,
    galleryPreviewProduct,
    isCoverPickerOpen,
    isSimplePDP,
    recencyExcludeSlotIndex,
    recencyExcludeStepId,
    selections
  ]);

  const shouldFetchCommittedOption =
    committedOptionProductFromParent === undefined && Boolean(mostRecentCommittedOptionProductId);

  const {
    data: mostRecentCommittedOptionProductResult,
    isFetching: isRecencyOptionFetching,
    isLoading: isRecencyOptionLoading,
  } = useProducts(
    {parameters: {ids: mostRecentCommittedOptionProductId ?? '', ...GET_PRODUCTS_PARAMS}},
    {enabled: shouldFetchCommittedOption, keepPreviousData: true} // hold prior recency row while id refetches
  );
  const mostRecentCommittedOptionProductFromFetch = findFetchedProductById(
    mostRecentCommittedOptionProductResult?.data,
    mostRecentCommittedOptionProductId
  );
  const mostRecentCommittedOptionProduct = committedOptionProductFromParent !== undefined ?
    (committedOptionProductFromParent ?? null) :
    mostRecentCommittedOptionProductFromFetch;

  // Complex flyout LHS — retain snapshot until recency/cover SCAPI settles (not on empty/error).
  const isRecencyViewDetailsPending =
    !isSimplePDP &&
    isComplexFlyoutScapiViewDetailsPending(
      committedOptionProductFromParent,
      mostRecentCommittedOptionProductId,
      mostRecentCommittedOptionProductFromFetch,
      isProductsFetchInFlight(
        shouldFetchCommittedOption,
        isRecencyOptionLoading,
        isRecencyOptionFetching
      )
    );

  const isCoverViewDetailsPending =
    !isSimplePDP &&
    isComplexFlyoutScapiViewDetailsPending(
      coverVariantProductFromParent,
      selectedCoverId,
      coverVariantProductFromFetch,
      isProductsFetchInFlight(shouldFetchCoverVariant, isCoverVariantLoading, isCoverVariantFetching)
    );

  // Defer cover SCAPI fallback in recency stack while either fetch is in flight.
  const suppressComplexCoverViewDetailsFallback =
    isRecencyViewDetailsPending || isCoverViewDetailsPending;

  const isComplexOptionPickerOpen =
    !isSimplePDP && activeSlot != null && activeSlot.stepId !== 'cover';

  // Keep flyoutDraftState.galleryPreview: SCAPI pending or option picker open (no deselect yet).
  const retainFlyoutLhsSnapshot =
    !galleryPreviewProduct &&
    (suppressComplexCoverViewDetailsFallback ||
      (isComplexOptionPickerOpen && !recencyExcludeSlot));

  const {mirrorGalleryProduct, viewDetailsProduct} = resolveCustomizeStepsGalleryState({
    activeSlot,
    coverVariantProduct,
    draftSelections: selections,
    galleryPreviewProduct,
    hasUserInteracted,
    isCoverPickerOpen,
    isRecencyViewDetailsPending,
    isSimplePDP,
    masterProduct: product,
    mostRecentCommittedOptionProduct,
    recencyExcludeSlot,
    selectedCoverId,
    suppressComplexCoverViewDetailsFallback,
  });

  // Complex flyout: single write path to flyoutDraftState.galleryPreview (Simple uses onSlotProductPreview).
  useEffect(() => {
    if (isSimplePDP) return;

    onGalleryProductChange?.({
      product: mirrorGalleryProduct,
      retainFlyoutLhsSnapshot,
      selectedVariationAttributes: coverVariationValues ?? {},
      viewDetailsProduct,
    });
  }, [
    coverVariationValues,
    isSimplePDP,
    mirrorGalleryProduct,
    onGalleryProductChange,
    retainFlyoutLhsSnapshot,
    viewDetailsProduct
  ]);

  useStepScrollHeader({steps: STEPS, activeSlot, stepRefs, scrollBodyRef, onHeaderChange, setActiveStepId});

  // Reset scroll only when ENTERING a sub-view.
  // When leaving (activeSlot → null), StepsMainView mounts fresh so scrollTop is
  // already 0 — an explicit reset here would fight with the scroll-to-step effect below.
  useEffect(() => {
    if (activeSlot !== null && scrollBodyRef.current) {
      scrollBodyRef.current.scrollTop = 0;
    }
  }, [activeSlot]);

  // Scroll to the next incomplete step after handleSlotNext returns to the main view.
  // Simple PDP mobile keeps window scroll (inline drawer); skip auto-advance like desktop flyout.
  useEffect(() => {
    if (activeSlot !== null || !pendingScrollStepRef.current) return;

    const step = pendingScrollStepRef.current;

    pendingScrollStepRef.current = null;

    if (isSimplePDP && isMobile) {
      return;
    }

    const timer = setTimeout(() => {
      if (isUserScrollingRef.current) return;

      let targetStep;

      if (step?.layoutType === 'slot-cards' && step.container?.values) {
        const stepSelections = selections?.[step.id] || [];
        const hasIncompleteSlots = step.container.values.some(
          (v, idx) => v.isRequired && stepSelections[idx] == null
        );

        if (hasIncompleteSlots) targetStep = step;
      }

      if (!targetStep) targetStep = findFirstIncompleteVisibleStep(STEPS, isStepComplete);

      const el = targetStep ? stepRefs.current[targetStep.id] : null;

      if (!el || !scrollBodyRef.current) return;

      el.scrollIntoView({behavior: 'smooth', block: 'start'});

      setActiveStepId(targetStep.id);
      onHeaderChange?.({
        isMultiSlotStep: isMultiSlotCustomizeStep(targetStep),
        customizeStepKind: getCustomizeStepSlotKind(targetStep),
        stepIndex: getVisibleStepDisplayIndex(targetStep.id, STEPS),
        title: getStepTitle(targetStep, intl),
        totalSteps: getVisibleCustomizeSteps(STEPS).length,
      });
    }, 50);

    return () => clearTimeout(timer);
  }, [activeSlot, selections, STEPS, isStepComplete, onHeaderChange, intl, isSimplePDP, isMobile]);

  // Pause auto-scroll when user manually scrolls
  useEffect(() => {
    const el = scrollBodyRef.current;

    if (!el) return;
    const handler = () => {
      isUserScrollingRef.current = true;
    };

    el.addEventListener('scroll', handler, {passive: true});

    return () => el.removeEventListener('scroll', handler);
  }, []);

  // Focus management: leaving picker → focus "Choose" button of completed slot
  useEffect(() => {
    if (!activeSlot && lastCompletedSlotRef.current) {
      const {slotIndex, stepId} = lastCompletedSlotRef.current;
      const el = slotButtonRefs.current[`${stepId}-${slotIndex}`];

      if (el) {
        try {
          el.focus({preventScroll: true});
        } catch {
          el.focus();
        }
      }
      lastCompletedSlotRef.current = null;
    }
  }, [activeSlot]);

  const priceProcessed = useMemo(() => {
    try {
      const raw = product?.c_priceProcessed;

      return raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
    } catch {
      return {};
    }
  }, [product]);

  const hasCoverData = coverGroups && coverGroups.length > 0;

  /** Panel chrome subtracted from mobile drawer height (header + sticky footer + inline gallery). */
  const MOBILE_CUSTOMIZE_GALLERY_HEADER_PX = 240;
  const bodyHeight = drawerHeight ?
    Math.max(
      0,
      drawerHeight - 64 - 80 - (isMobile ? MOBILE_CUSTOMIZE_GALLERY_HEADER_PX : 0)
    ) :
    undefined;
  const activeStepData = activeSlot ? STEPS.find((s) => s.id === activeSlot.stepId) : null;

  return {
    STEPS,
    activeSlot,
    activeStepData,
    activeStepId,
    bodyHeight,
    currency: product?.currency ?? 'USD',
    galleryPreviewProduct,
    optionPreviewCleared: optionPreviewCleared === (activeSlot ? `${activeSlot.stepId}-${activeSlot.slotIndex}` : null),
    hasCoverData,
    isMobile,
    isStepComplete,
    liveMessage,
    masterMinPrice: priceProcessed?.min ?? priceProcessed?.salePrice ?? null,
    scrollBodyRef,
    slotButtonRefs,
    stepRefs,
    handleBack,
    handleCoverVariantViewDetails,
    handleMainNext,
    handleOptionProductViewDetails,
    handleSlotChoose,
    handleSlotNext,
    handleSlotProductPreview,
    handleStepInteraction,
  };
}
